// ─── The page ────────────────────────────────────────────────────────────────
//
// This is the whole point of `@nulife/chassis`, and the thing that did not
// exist before it.
//
// `@nulife/design-language` shares the PIECES — AppFrame, Hero, Tile, the
// watermark system. Every app still wrote its own `Home.tsx` that ASSEMBLED
// those pieces into a page: its own grid, its own banner wiring, its own
// decision about which tiles get artwork. That assembly step is where all
// three divergences actually happened. Sharing parts but not the page leaves a
// design decision on the table for whoever builds app number four.
//
// So this file is the page, shared. `<NulifeApp>` renders 100% of the shell —
// banners, header, hero, pulse strip, tile grid, the ADD sheet, the live-status
// viewer, the module strip, the Ask Mira bar — and takes CONTENT ONLY. There
// is no assembly step left to get wrong, because there is no assembly step.
//
// ── Where it came from ──────────────────────────────────────────────────────
//
// Lifted from `nulife-events-manager/client/src/pages/NuLifeHome.tsx` as it
// stood at commit ead4fc2 — a literal extraction, not a re-derivation. Every
// element, class, style object and nesting level below is that file's. What
// changed is only WHERE THE DATA COMES FROM: `NuLifeHome` reaches for tRPC,
// wouter and Farndale's organisation settings; this takes the same values as
// typed props. `proof/` carries the pixel diff that says so.
//
// ── The guardrail this file has to hold ─────────────────────────────────────
//
// There is no `style`, `className`, `render` or slot prop that lets a
// consuming app touch shell layout, spacing or the reset. Not one. If an app
// needs something the shell does not do, that is a chassis PR flagged to Drea
// before it is written — never a local workaround. The props below are the
// complete, Drea-approved configurable surface: a tile registry, branding
// tokens, and the app's own screens. Widening it is a question, not a
// judgement call.

import { useState, type ReactNode } from "react";
import {
  AppFrame, Hero, PulseStrip, ModuleStrip, type PulseItem, type ModuleItem,
  type HeroAction, type LiveStatusRow, type AskMiraMode,
} from "./chrome";
import { Tile, type WatermarkMotif, type TileMetric } from "./primitives";
import { DataState, type LoadState } from "./state";
import { TimelineViewer } from "./timeline";

// ─── The configurable surface, and all of it ─────────────────────────────────

/**
 * One tile on the home grid.
 *
 * An app decides WHICH tiles it has and what each is called and does. It does
 * not decide how a tile looks or lays out — there is deliberately no way to
 * express that here.
 */
export type ChassisTile = {
  id: string;
  label: string;
  /** From the chassis's own approved motif set. A concept with no real artwork
   *  uses `"none"`, which is Farndale's own "nothing yet" state: the Watermark
   *  renders nothing at all. Drawing a new placeholder shape per concept is
   *  not permitted, however faithfully or however faint. */
  motif?: WatermarkMotif;
  /** Where the tile goes. Passed back to `onNavigate`; the chassis owns
   *  getting the user there and knows nothing about what is on the far side. */
  path?: string;
  /** Zero to three. "A tile only gets a live number when that number would
   *  change what someone does next" — an asymmetric grid is correct. */
  metrics?: TileMetric[];
  /** No module behind it yet. Says so in words; not a link. */
  comingSoon?: boolean;
  comingSoonLabel?: string;
  hint?: string;
  /** Makes the tile a real anchor as well as a handler, so middle-click and
   *  open-in-new-tab work. Usually the same value as `path`. */
  href?: string;
};

/** One destination on the persistent module strip. */
export type ChassisModule = Omit<ModuleItem, "onPress"> & { path?: string };

/**
 * A strip above everything, for saying plainly that the page is showing
 * something other than the present.
 *
 * Typed rather than a `ReactNode` slot, and deliberately: a node slot is a
 * hole in guardrail 1 through which an app can put its own layout into the
 * shell. The three tones below are exactly the three treatments Farndale's
 * own Home renders and nothing more — an app supplies the words, the chassis
 * owns the furniture.
 */
export type ChassisNotice = {
  id: string;
  /** `info` is Farndale's badge-opt-in strip, `active` its "previewing show
   *  day" strip, `quiet` its "the show is 31 August" offer. */
  tone?: "info" | "active" | "quiet";
  message: ReactNode;
  actions?: {
    label: string;
    onPress: () => void;
    /** `strong` is full ink (an action being offered), `normal` is dim ink,
     *  `faint` is a dismissal. Farndale uses all three. */
    emphasis?: "strong" | "normal" | "faint";
    /** Pushed to the far end of the strip — Farndale's Dismiss and its Back
     *  to now. */
    align?: "end";
  }[];
};

const NOTICE_TONE = {
  info:   { pad: "px-4 py-2 text-sm",   bg: "var(--nl-bg-raised)", stroke: "var(--nl-stroke)",        ink: "var(--nl-ink-dim)" },
  active: { pad: "px-4 py-2 text-sm",   bg: "var(--nl-tile-hover)", stroke: "var(--nl-stroke-strong)", ink: "var(--nl-ink)" },
  quiet:  { pad: "px-4 py-1.5 text-xs", bg: "var(--nl-bg-raised)", stroke: "var(--nl-stroke)",        ink: "var(--nl-ink-faint)" },
} as const;

const ACTION_INK = {
  strong: "var(--nl-ink)",
  normal: "var(--nl-ink-dim)",
  faint:  "var(--nl-ink-faint)",
} as const;

function Notices({ notices }: { notices: ChassisNotice[] }) {
  return (
    <>
      {notices.map(n => {
        const tone = NOTICE_TONE[n.tone ?? "info"];
        return (
          <div
            key={n.id}
            data-testid="chassis-notice"
            className={`flex flex-wrap items-center gap-3 ${tone.pad}`}
            style={{ background: tone.bg, borderBottom: `1px solid ${tone.stroke}` }}
          >
            <span style={{ color: tone.ink }}>{n.message}</span>
            {(n.actions ?? []).map(a => (
              <button
                key={a.label}
                type="button"
                onClick={a.onPress}
                className={`${a.align === "end" ? "ml-auto " : ""}text-xs uppercase tracking-wide`}
                style={{
                  color: ACTION_INK[a.emphasis ?? "normal"],
                  background: "none", border: 0, cursor: "pointer",
                }}
              >{a.label}</button>
            ))}
          </div>
        );
      })}
    </>
  );
}

export type ChassisAddAction = { id: string; label: string };

export type NulifeAppProps = {
  // ── Branding ──────────────────────────────────────────────────────────────
  /** The hero title. Farndale's is its event's name. */
  title: string;
  /** The organisation's own mark, beside the title. Absent takes no space and
   *  renders no wrapper. */
  logo?: ReactNode;
  /** A quiet second line under the title. */
  subtitle?: ReactNode;
  /** Palette selection — the value of `data-nl-design`, from the approved
   *  token-driven theme system. Not a colour, a choice between token sets. */
  theme?: string;

  // ── Content ───────────────────────────────────────────────────────────────
  tiles: ChassisTile[];
  modules?: ChassisModule[];
  pulse?: PulseItem[];
  pulseState?: "ready" | "loading" | "unavailable";

  /** The hero's Live Status rows. `LoadState` rather than an array because
   *  loading, verified-empty and unavailable must stay distinguishable all the
   *  way to the screen — see state.tsx. */
  liveStatus?: LoadState<LiveStatusRow[]>;
  /** What the strip is called. "Live status:" reads right for a show; a hall
   *  names the same strip differently. */
  liveStatusLabel?: string;
  /** The verified-empty line. Phrase it as an all-clear, not an absence. */
  liveStatusEmptyMessage?: string;
  /** The span a tapped Live Status row is drawn against. */
  liveStatusRange?: { start: Date; end: Date };
  /** Where "View all" and an expanded strip's "Schedule" button go. */
  liveStatusPath?: string;

  notices?: ChassisNotice[];
  /**
   * App infrastructure that renders its OWN strip above the notices — the PWA
   * install prompt and the service-worker "a new version is ready" banner.
   *
   * These are not shell layout and not an escape hatch for it: each is a
   * self-contained component reading a per-app browser channel the chassis
   * cannot know about. They are named individually rather than offered as one
   * general slot so that adding a third is a deliberate act.
   */
  installBanner?: ReactNode;
  updateBanner?: ReactNode;

  /** The ADD control. Omit it and the hero has no ADD button, exactly as
   *  Farndale's own hero does when `onAdd` is absent. */
  add?: {
    actions: LoadState<ChassisAddAction[]>;
    onSelect: (id: string) => void;
  };
  /** The second, smaller action beside ADD. */
  secondaryAction?: HeroAction;

  /** Every destination in the shell comes back through here. The chassis owns
   *  getting the user there; the app owns what a path means. */
  onNavigate?: (path: string) => void;

  onAskMira?: (text: string, mode: AskMiraMode) => void;
  voiceAvailable?: boolean;
  /** Overrides "Ask Mira AI" where an app's assistant has another name. */
  assistantLabel?: string;
  /** Set when this app has no assistant wired yet — the bar says so and
   *  cannot be pressed, rather than opening onto nothing. */
  assistantUnavailable?: string;

  /** The app's own screen, rendered in the chassis's content region INSTEAD of
   *  the tile grid — this is how a module's destination is shown. The chassis
   *  does not know or care what is inside; it owns the shell around it. */
  children?: ReactNode;
  /**
   * App content above and below the tile grid, on the home screen.
   *
   * CONTENT, not a layout hatch, and the distinction is the one guardrail 1
   * turns on: the chassis still owns the grid, the content region, its padding
   * and its scrolling. These place an app's own block inside that region, the
   * same way `children` places a whole module screen in it. Nothing here can
   * change the shell.
   *
   * Memorial Hall needs both — an acknowledge-these list above the grid, and a
   * caption plus sign-out below it — and neither belongs to the shell.
   */
  beforeTiles?: ReactNode;
  afterTiles?: ReactNode;
  /** Back to the grid, and back one step. Present on a module screen, absent
   *  on the home screen — same rule as Farndale's AppFrame. */
  onHome?: () => void;
  onBack?: () => void;
  /** The module screen's own title in the back bar. */
  screenTitle?: string;
};

/**
 * The NULIFE shell. One component, every app, no assembly.
 */
export function NulifeApp({
  title, logo, subtitle, theme,
  tiles, modules = [], pulse = [], pulseState = "ready",
  liveStatus = { status: "empty" }, liveStatusLabel, liveStatusEmptyMessage,
  liveStatusRange, liveStatusPath,
  notices = [], installBanner, updateBanner,
  add, secondaryAction, onNavigate,
  onAskMira, voiceAvailable, assistantLabel, assistantUnavailable,
  children, beforeTiles, afterTiles, onHome, onBack, screenTitle,
}: NulifeAppProps) {
  const [addOpen, setAddOpen] = useState(false);
  const [viewerRow, setViewerRow] = useState<LiveStatusRow | null>(null);

  const go = (path?: string) => { if (path) onNavigate?.(path); };
  const rows = liveStatus;

  return (
    <AppFrame
      theme={theme}
      onHome={onHome}
      onBack={onBack}
      title={screenTitle}
      updateBanner={updateBanner}
      assistantLabel={assistantLabel}
      assistantUnavailable={assistantUnavailable}
      onAskMira={onAskMira}
      voiceAvailable={voiceAvailable}
      banner={
        <>
          {installBanner}
          <Notices notices={notices} />
        </>
      }
      hero={
        <Hero
          title={title}
          logo={logo}
          subtitle={subtitle}
          rows={rows}
          statusLabel={liveStatusLabel}
          emptyMessage={liveStatusEmptyMessage}
          onAdd={add ? () => setAddOpen(true) : undefined}
          secondaryAction={secondaryAction}
          onOpenRow={id => setViewerRow(
            rows.status === "ready" ? rows.data.find(r => r.id === id) ?? null : null
          )}
          onViewAll={liveStatusPath ? () => go(liveStatusPath) : undefined}
          onOpenSchedule={liveStatusPath ? () => go(liveStatusPath) : undefined}
        />
      }
      pulse={<PulseStrip items={pulse} state={pulseState} />}
      modules={
        <ModuleStrip
          items={modules.map(m => ({ ...m, onPress: () => go(m.path) }))}
        />
      }
    >
      {/* A module screen takes the content region; the grid IS the home
          screen. One shell, two contents — never two shells. */}
      {children ?? (
        <>
        {beforeTiles}
        <div className="grid grid-cols-2 gap-3" data-testid="home-tiles">
          {tiles.map(t => (
            <Tile
              key={t.id}
              label={t.label}
              motif={t.motif ?? "none"}
              metrics={t.metrics ?? []}
              comingSoon={t.comingSoon}
              comingSoonLabel={t.comingSoonLabel}
              hint={t.hint}
              href={t.href}
              onPress={t.path ? () => go(t.path) : undefined}
              tall
              // Dark-ish with a bright border (Drea, 2026-08-17) — light was an
              // overcorrection, the original was too dark, and the border is
              // what should attract the eye.
              tone="module"
            />
          ))}
        </div>
        {afterTiles}
        </>
      )}

      {/* The ADD control is "a quick-action doorway, not direct navigation to
          one Add screen" — a permission-aware menu of what THIS user may
          create right now. Actions they can't perform are absent, not
          disabled. */}
      {add && addOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center"
          style={{ background: "var(--nl-scrim)" }}
          onClick={() => setAddOpen(false)}
          role="dialog" aria-modal="true" aria-label="Add"
        >
          <div
            onClick={e => e.stopPropagation()}
            className="w-full max-w-md p-4"
            style={{
              background: "var(--nl-bg-raised)", border: "1px solid var(--nl-stroke)",
              borderRadius: "var(--nl-radius-lg) var(--nl-radius-lg) 0 0",
            }}
          >
            <div className="mb-3 text-lg" style={{ color: "var(--nl-ink)" }}>Add</div>
            <DataState
              label="Add actions"
              emptyMessage="Nothing you can create here"
              state={add.actions}
            >
              {actions => (
                <div className="flex flex-col gap-2">
                  {actions.map(a => (
                    <button
                      key={a.id} type="button"
                      className="px-4 py-3 text-left text-base"
                      style={{
                        borderRadius: "var(--nl-radius-sm)", background: "var(--nl-tile)",
                        border: "1px solid var(--nl-stroke)", color: "var(--nl-ink)",
                        cursor: "pointer", minHeight: "var(--nl-hit-min)",
                      }}
                      onClick={() => { setAddOpen(false); add.onSelect(a.id); }}
                    >
                      {a.label}
                    </button>
                  ))}
                </div>
              )}
            </DataState>
          </div>
        </div>
      )}

      {viewerRow && (
        <TimelineViewer
          title={viewerRow.label}
          window={{ startsAt: new Date(Date.now() - 6 * 3600_000), endsAt: new Date(Date.now() + 6 * 3600_000) }}
          segments={viewerRow.segments}
          rangeStart={liveStatusRange?.start ?? new Date()}
          rangeEnd={liveStatusRange?.end ?? new Date()}
          activeDay={new Date()}
          onClose={() => setViewerRow(null)}
        />
      )}
    </AppFrame>
  );
}
